import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../utils/api';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, analyticsData] = await Promise.all([
        analyticsAPI.getStats(),
        analyticsAPI.getDashboard(30)
      ]);
      setStats(statsData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Загрузка...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Панель управления</h1>
        <p className="text-gray-600 mt-2">Обзор ключевых метрик и статистики</p>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Всего заявок"
            value={stats.inquiries?.total || 0}
            subtitle={`Сегодня: ${stats.inquiries?.today || 0}`}
            color="blue"
            icon="📨"
          />
          <StatCard
            title="Новые заявки"
            value={stats.inquiries?.new || 0}
            subtitle="Требуют обработки"
            color="orange"
            icon="🔔"
          />
          <StatCard
            title="Активные услуги"
            value={stats.services?.active || 0}
            subtitle={`Всего: ${stats.services?.total || 0}`}
            color="green"
            icon="🛠️"
          />
          <StatCard
            title="В обработке"
            value={stats.inquiries?.in_progress || 0}
            subtitle="Заявки"
            color="purple"
            icon="⏳"
          />
        </div>
      )}

      {/* Charts Section */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Inquiries */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Последние заявки</h2>
            <div className="space-y-3">
              {analytics.recent_inquiries?.slice(0, 5).map((inquiry) => (
                <div key={inquiry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{inquiry.name}</p>
                    <p className="text-sm text-gray-600">{inquiry.email}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(inquiry.status)}`}>
                    {getStatusLabel(inquiry.status)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Inquiries by Status */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Заявки по статусам</h2>
            <div className="space-y-3">
              {Object.entries(analytics.inquiries_by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-gray-700">{getStatusLabel(status)}</span>
                  <span className="font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Popular Services */}
          {analytics.popular_services?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6 lg:col-span-2">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Популярные услуги</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {analytics.popular_services.slice(0, 6).map((service, idx) => (
                  <div key={service.service_id || idx} className="p-4 bg-blue-50 rounded-lg">
                    <p className="font-medium text-gray-900 mb-1">{service.title_ru || service.title || '—'}</p>
                    <p className="text-sm text-gray-600">{service.views} просмотров</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, subtitle, color, icon }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg ${colors[color]} flex items-center justify-center text-2xl`}>
          {icon}
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
      <p className="text-gray-600 font-medium mt-1">{title}</p>
      {subtitle && <p className="text-sm text-gray-500 mt-2">{subtitle}</p>}
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    new: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const getStatusLabel = (status) => {
  const labels = {
    new: 'Новая',
    in_progress: 'В обработке',
    completed: 'Завершена',
    cancelled: 'Отменена',
  };
  return labels[status] || status;
};

export default Dashboard;
